import { Nullable, NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IPolicy } from "../contracts/policies/policy";
import { IUserGroupPolicy, UserGroupPolicy } from "../contracts/policies/user-group-policy";
import { IUserPolicy, UserPolicy } from "../contracts/policies/user-policy";
import { InternalError } from "../errors/internal-error";
import Guard from "../guard";
import { PolicyResource } from "../models/policies/policy-resource";
import { MappingUtilities } from "./mapping-utilities";
import { MemberSecurityMapper } from "./member-security-mapper";
import { RulesMapper } from "./rules-mapper";

export class PolicyMapper {
    /**
     * Map a policy resource to a user group policy instance.
     * @param client - An instance of an extended Content Hub client
     * @param ruleResource - A policy resource
     * @returns a user group policy or null.
     */
    static async mapToUserGroupPolicyAsync(
        client: IExtendedContentHubClient,
        policyResource: Nullable<PolicyResource>
    ): NullableResultPromise<IUserGroupPolicy> {
        if (policyResource == null) {
            return null;
        }

        Guard.notNullOrUndefined(policyResource.userGroup);

        const userGroupId = await client.linkHelper.idFromEntityAsync(policyResource.userGroup!);

        Guard.notNull(userGroupId);
        Guard.validId(userGroupId!);

        const createdBy = await client.linkHelper.idFromEntityAsync(policyResource.createdBy);
        const modifiedBy = await client.linkHelper.idFromEntityAsync(policyResource.modifiedBy);
        const memberSecurity = await MemberSecurityMapper.mapToMemberPoliciesAsync(
            client,
            policyResource.memberSecurity
        );
        const memberGroupSecurity = await MemberSecurityMapper.mapToMemberPoliciesAsync(
            client,
            policyResource.memberGroupSecurity
        );
        const rules = await RulesMapper.mapToRulesAsync(client, policyResource.rules);

        const userGroupPolicy: UserGroupPolicy = new UserGroupPolicy({
            memberSecurity: memberSecurity,
            memberGroupSecurity: memberGroupSecurity,
            rules: rules,
            userGroupId: userGroupId!,
            createdBy: createdBy,
            createdOn: MappingUtilities.toNullableDate(policyResource.createdOn),
            modifiedBy: modifiedBy,
            modifiedOn: MappingUtilities.toNullableDate(policyResource.modifiedOn),
        });

        return userGroupPolicy;
    }

    /**
     * Map a policy resource to a user policy instance.
     * @param client - An instance of an extended Content Hub client
     * @param policyResource - A policy resource
     * @returns a user policy or null.
     */
    static async mapToUserPolicyAsync(
        client: IExtendedContentHubClient,
        policyResource: Nullable<PolicyResource>
    ): NullableResultPromise<IUserPolicy> {
        if (policyResource == null) {
            return null;
        }

        Guard.notNullOrUndefined(policyResource.user);

        const userId = await client.linkHelper.idFromEntityAsync(policyResource.user!);

        Guard.notNull(userId);
        Guard.validId(userId!);

        const createdBy = await client.linkHelper.idFromEntityAsync(policyResource.createdBy);
        const modifiedBy = await client.linkHelper.idFromEntityAsync(policyResource.modifiedBy);
        const memberSecurity = await MemberSecurityMapper.mapToMemberPoliciesAsync(
            client,
            policyResource.memberSecurity
        );
        const memberGroupSecurity = await MemberSecurityMapper.mapToMemberPoliciesAsync(
            client,
            policyResource.memberGroupSecurity
        );
        const rules = await RulesMapper.mapToRulesAsync(client, policyResource.rules);

        const userPolicy = new UserPolicy({
            memberSecurity: memberSecurity,
            memberGroupSecurity: memberGroupSecurity,
            rules: rules,
            userId: userId!,
            createdBy: createdBy,
            createdOn: MappingUtilities.toNullableDate(policyResource.createdOn),
            modifiedBy: modifiedBy,
            modifiedOn: MappingUtilities.toNullableDate(policyResource.modifiedOn),
        });

        return userPolicy;
    }

    /**
     * Map a user (group) policy instance to a policy resource.
     * @param client - An instance of an extended Content Hub client
     * @param policy - A user policy or user group policy instance
     * @returns A policy resource.
     */
    static async mapToResourceAsync(
        client: IExtendedContentHubClient,
        policy: Nullable<IPolicy>
    ): NullableResultPromise<PolicyResource> {
        if (policy == null) {
            return null;
        }

        const rules = await RulesMapper.mapToRuleResourcesAsync(client, policy.rules);
        const resource = new PolicyResource();
        resource.rules = rules;

        if (policy instanceof UserPolicy) {
            const user = await client.linkHelper.entityToLinkAsync(policy.userId);
            resource.user = user;
        } else if (policy instanceof UserGroupPolicy) {
            const userGroup = await client.linkHelper.entityToLinkAsync(policy.userGroupId);
            resource.userGroup = userGroup;
        } else {
            throw new InternalError("Policy type is not supported.");
        }

        const memberSecurity = await MemberSecurityMapper.mapToMemberPolicyResourcesAsync(
            client,
            policy.memberSecurity
        );
        resource.memberSecurity = memberSecurity;

        const groupSecurity = await MemberSecurityMapper.mapToMemberPolicyResourcesAsync(
            client,
            policy.memberGroupSecurity
        );
        resource.memberGroupSecurity = groupSecurity;

        return resource;
    }
}
